import React, { useEffect, useRef, useState } from 'react';
import { scoreCamoEffectiveness } from '../utils/camoEffectivenessScore';
import './CamoEffectivenessReport.css';

function ScoreRing({ score, size = 88 }) {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="score-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle className="score-ring-bg" cx={size / 2} cy={size / 2} r={radius} fill="none" strokeWidth="6" />
        <circle
          className="score-ring-fill"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth="6"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="score-ring-value">
        <span className="score-number">{score}</span>
      </div>
    </div>
  );
}

function MetricBreakdown({ metricDetails }) {
  if (!metricDetails?.length) return null;

  return (
    <ul className="metric-breakdown">
      {metricDetails.map((metric) => (
        <li key={metric.key} className="metric-breakdown-item">
          <div className="metric-breakdown-header">
            <span className="metric-breakdown-label">{metric.label}</span>
            <span className="metric-breakdown-score">{metric.score}</span>
          </div>
          <div className="metric-breakdown-bar-track" aria-hidden="true">
            <div
              className="metric-breakdown-bar-fill"
              style={{ width: `${metric.score}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

function PlacementPreviewCarousel({ previews, stats }) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);

  const items = previews?.length
    ? previews
    : stats?.previews?.map((p, i) => ({
        index: i + 1,
        total: stats.previews.length,
        score: Math.round(p.score),
        tag: i === 0 ? 'Worst' : i === 1 ? 'Median' : i === 2 ? 'Best' : null,
        url: p.url,
      })) ?? [];

  useEffect(() => {
    setIndex(0);
  }, [previews, stats]);

  if (!items.length) return null;

  const total = items.length;
  const current = items[index];
  const canPrev = index > 0;
  const canNext = index < total - 1;

  const goPrev = () => {
    if (canPrev) setIndex((i) => i - 1);
  };

  const goNext = () => {
    if (canNext) setIndex((i) => i + 1);
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 48) {
      if (delta < 0) goNext();
      else goPrev();
    }
    touchStartX.current = null;
  };

  const handleStageClick = (e) => {
    if (e.target.closest('.placement-carousel-nav')) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (x < rect.width * 0.35) goPrev();
    else if (x > rect.width * 0.65) goNext();
  };

  return (
    <div className="placement-carousel">
      <div className="placement-carousel-header">
        <span className="placement-carousel-title">Blend tests</span>
        <span className="placement-carousel-counter">
          {index + 1} / {total}
        </span>
      </div>

      <div
        className="placement-carousel-stage"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onClick={handleStageClick}
        role="region"
        aria-label="Camo placement preview"
        aria-roledescription="carousel"
      >
        <button
          type="button"
          className="placement-carousel-nav placement-carousel-nav-prev"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          disabled={!canPrev}
          aria-label="Previous placement"
        >
          ‹
        </button>

        <img
          key={`${current.index}-${current.url}`}
          src={current.url}
          alt={`Camo blend test ${current.index} of ${total}`}
          className="placement-carousel-image"
          draggable={false}
        />

        <button
          type="button"
          className="placement-carousel-nav placement-carousel-nav-next"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          disabled={!canNext}
          aria-label="Next placement"
        >
          ›
        </button>
      </div>

      <div className="placement-carousel-caption">
        {current.tag && (
          <span className={`placement-carousel-tag placement-carousel-tag-${current.tag.toLowerCase()}`}>
            {current.tag}
          </span>
        )}
        <span className="placement-carousel-score">Score {current.score}</span>
        {stats && (
          <span className="placement-carousel-range">
            Range {stats.worstCase}–{stats.bestCase} · median {stats.median}
          </span>
        )}
      </div>
    </div>
  );
}

const CamoEffectivenessReport = ({
  environmentImageUrl,
  patternImageUrl,
  variant,
  mode = 'summary',
  report: externalReport,
  loading: externalLoading = false,
  isRecommended = false,
}) => {
  const [report, setReport] = useState(externalReport ?? null);
  const [loading, setLoading] = useState(externalLoading);
  const [error, setError] = useState(null);

  const isControlled = externalReport !== undefined || externalLoading;

  useEffect(() => {
    if (isControlled) {
      setReport(externalReport ?? null);
      setLoading(externalLoading);
      return;
    }

    if (!environmentImageUrl || !patternImageUrl) return;

    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await scoreCamoEffectiveness(environmentImageUrl, patternImageUrl);
        if (!cancelled) setReport(result);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Score unavailable');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [environmentImageUrl, patternImageUrl, externalReport, externalLoading, isControlled]);

  if (loading) {
    return (
      <div className={`camo-effectiveness ${mode}`}>
        <div className="effectiveness-loading compact-loading">
          <span className="effectiveness-spinner" />
          <span>Scoring…</span>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className={`camo-effectiveness ${mode}`}>
        <p className="effectiveness-error">Score unavailable</p>
      </div>
    );
  }

  if (mode === 'compact') {
    return (
      <div className={`camo-effectiveness compact ${isRecommended ? 'recommended' : ''}`}>
        {isRecommended && <span className="recommended-badge">Best</span>}
        <ScoreRing score={report.overall} size={64} />
        <span className={`tier-badge ${report.tier.className}`}>{report.tier.label}</span>
      </div>
    );
  }

  return (
    <div className={`camo-effectiveness summary ${isRecommended ? 'recommended' : ''}`}>
      <div className="summary-score-row">
        <ScoreRing score={report.overall} size={56} />
        <div className="summary-meta">
          <span className="summary-label">
            {variant ? `Design ${variant}` : 'Match score'}
          </span>
          <span className={`tier-badge ${report.tier.className}`}>{report.tier.label}</span>
          {isRecommended && <span className="summary-best-badge">Best match</span>}
        </div>
      </div>

      <MetricBreakdown metricDetails={report.metricDetails} />

      <PlacementPreviewCarousel
        previews={report.placementAnalysis?.allPreviews}
        stats={report.placementAnalysis}
      />
    </div>
  );
};

export default CamoEffectivenessReport;
